import React, { useState, useMemo } from 'react';
import { Table, Badge, Form, InputGroup, Spinner, Button } from 'react-bootstrap';
import { ArrowUpDown, Search, Eye } from 'lucide-react';

const formatCurrency = (n) =>
  new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(n || 0);

const formatPct = (n) => `${((n || 0) * 100).toFixed(1)}%`;

const SORT_OPTIONS = [
  { value: 'repurchase_rate', label: 'Tỉ lệ mua lại' },
  { value: 'loyal_rate', label: 'Tỉ lệ loyal' },
  { value: 'total_customers', label: 'Tổng khách' },
  { value: 'total_orders', label: 'Tổng đơn' },
  { value: 'total_revenue', label: 'Doanh thu' },
];

const RepurchaseBadge = ({ rate }) => {
  let variant = 'secondary';
  if (rate >= 0.3) variant = 'success';
  else if (rate >= 0.15) variant = 'primary';
  else if (rate >= 0.05) variant = 'warning';
  return <Badge bg={variant}>{formatPct(rate)}</Badge>;
};

const ProductRepurchaseTable = ({
  data,
  loading,
  sort,
  onSortChange,
  onSelectProduct,
  selectedProductId,
}) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!data?.rows) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return data.rows;
    return data.rows.filter(
      (r) =>
        r.menu_item_name.toLowerCase().includes(needle) ||
        (r.category_name || '').toLowerCase().includes(needle),
    );
  }, [data, search]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <InputGroup style={{ maxWidth: 320 }}>
          <InputGroup.Text>
            <Search size={16} />
          </InputGroup.Text>
          <Form.Control
            placeholder="Tìm món hoặc danh mục…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        <div className="d-flex align-items-center gap-2">
          <ArrowUpDown size={16} className="text-muted" />
          <span className="text-muted small">Sort:</span>
          <Form.Select
            size="sm"
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
            style={{ width: 200 }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Form.Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" /> <span className="ms-2">Đang tải…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-5 text-muted">Không có dữ liệu trong khoảng thời gian đã chọn.</div>
      ) : (
        <div className="table-responsive">
          <Table hover className="align-middle">
            <thead className="table-light">
              <tr>
                <th>Món</th>
                <th className="text-center">Tổng khách</th>
                <th className="text-center">1 lần</th>
                <th className="text-center">2 lần</th>
                <th className="text-center">3 lần</th>
                <th className="text-center">4 lần</th>
                <th className="text-center">≥5 lần</th>
                <th className="text-center">Tỉ lệ mua lại</th>
                <th className="text-center">Tỉ lệ loyal</th>
                <th className="text-end">Tổng đơn</th>
                <th className="text-end">Doanh thu</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.menu_item_id}
                  className={selectedProductId === r.menu_item_id ? 'table-primary' : ''}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onSelectProduct(r)}
                >
                  <td>
                    <div className="fw-semibold">{r.menu_item_name}</div>
                    {r.category_name && (
                      <div className="small text-muted">{r.category_name}</div>
                    )}
                  </td>
                  <td className="text-center">{r.total_customers.toLocaleString('vi-VN')}</td>
                  <td className="text-center text-muted">{r.buyers_1x}</td>
                  <td className="text-center">{r.buyers_2x}</td>
                  <td className="text-center">{r.buyers_3x}</td>
                  <td className="text-center">{r.buyers_4x}</td>
                  <td className="text-center">
                    <Badge bg={r.buyers_5x_plus > 0 ? 'success' : 'light'} text={r.buyers_5x_plus > 0 ? 'white' : 'dark'}>
                      {r.buyers_5x_plus}
                    </Badge>
                  </td>
                  <td className="text-center"><RepurchaseBadge rate={r.repurchase_rate} /></td>
                  <td className="text-center"><RepurchaseBadge rate={r.loyal_rate} /></td>
                  <td className="text-end">{r.total_orders.toLocaleString('vi-VN')}</td>
                  <td className="text-end">{formatCurrency(r.total_revenue)} ₫</td>
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectProduct(r);
                      }}
                    >
                      <Eye size={14} /> Khách
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ProductRepurchaseTable;
